import type { Scene } from 'three';
import {
  AdditiveBlending,
  BoxGeometry,
  ConeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  Vector3,
} from 'three';

const scratchMid = new Vector3();
const scratchDirection = new Vector3();
const scratchQuat = new Quaternion();
/** The cone's axis is +Y; the rope and the box are built along +Z. */
const HOOK_TILT = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2);

/**
 * The magnetic grapple hook and its line.
 *
 * The hook is invisible in the simulation: `GrappleHook` only tracks a point
 * travelling along a ray. This draws that line, from the muzzle the player
 * can see to wherever the hook currently is, so firing the tool reads as a
 * hook thrown at a wall (or at a drone) instead of an invisible state change.
 */
export class GrappleView {
  readonly group = new Group();

  private readonly rope: Mesh;
  private readonly hook: Mesh;

  constructor(scene: Scene) {
    const ropeMaterial = new MeshBasicMaterial({
      color: 0x63f0d0,
      transparent: true,
      opacity: 0.85,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.rope = new Mesh(new BoxGeometry(0.03, 0.03, 1), ropeMaterial);
    this.hook = new Mesh(
      new ConeGeometry(0.11, 0.32, 6),
      new MeshBasicMaterial({ color: 0xdfe8ff }),
    );
    this.group.add(this.rope, this.hook);
    this.group.visible = false;
    scene.add(this.group);
  }

  /**
   * Draw the line from `from` (the muzzle) to `to` (the hook tip or the
   * anchor it is fastened to). A degenerate line hides the view instead of
   * leaving a zero-length box on screen.
   */
  show(from: Vector3, to: Vector3): void {
    scratchDirection.subVectors(to, from);
    const length = scratchDirection.length();
    if (length < 1e-4) {
      this.hide();
      return;
    }

    scratchMid.copy(from).add(to).multiplyScalar(0.5);
    this.rope.position.copy(scratchMid);
    this.rope.scale.set(1, 1, length);
    this.rope.lookAt(to);

    this.hook.position.copy(to);
    scratchQuat.copy(this.rope.quaternion).multiply(HOOK_TILT);
    this.hook.quaternion.copy(scratchQuat);

    this.group.visible = true;
  }

  hide(): void {
    this.group.visible = false;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.rope.geometry.dispose();
    this.hook.geometry.dispose();
    (this.rope.material as MeshBasicMaterial).dispose();
    (this.hook.material as MeshBasicMaterial).dispose();
  }
}
